import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, TagIcon, ImageIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
type GalleryFilter = 'posted' | 'tagged';
interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  type: 'posted' | 'tagged';
  caption?: string;
  credit?: string;
}
const galleryImages: GalleryImage[] = [{
  id: '1',
  src: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=600&q=80',
  alt: 'Unboxing photo',
  type: 'posted',
  caption: 'March box reveal 📦'
}, {
  id: '2',
  src: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80',
  alt: 'Bookmark close-up',
  type: 'posted',
  caption: "This month's exclusive bookmark"
}, {
  id: '3',
  src: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&q=80',
  alt: 'Bookshelf with box items',
  type: 'tagged',
  credit: '@bookishreader',
  caption: 'Love my new additions!'
}, {
  id: '4',
  src: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=500&q=80',
  alt: 'Reading nook setup',
  type: 'tagged',
  credit: '@cozyreads',
  caption: 'Perfect reading corner'
}, {
  id: '5',
  src: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=600&q=80',
  alt: 'Box contents flat lay',
  type: 'posted',
  caption: 'February box — all items'
}, {
  id: '6',
  src: 'https://images.unsplash.com/photo-1524578271613-d550eacf6090?w=400&q=80',
  alt: 'Candle and book',
  type: 'tagged',
  credit: '@litvibes',
  caption: 'Evening reading ritual 🕯️'
}, {
  id: '7',
  src: 'https://images.unsplash.com/photo-1495640388908-05fa85288e61?w=700&q=80',
  alt: 'Book stack',
  type: 'posted',
  caption: 'Q1 collection 📚'
}, {
  id: '8',
  src: 'https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=500&q=80',
  alt: 'Open book',
  type: 'tagged',
  credit: '@pageturner99',
  caption: 'Already halfway through!'
}, {
  id: '9',
  src: 'https://images.unsplash.com/photo-1535905557558-afc4877a26fc?w=600&q=80',
  alt: 'Merch items',
  type: 'posted',
  caption: 'Exclusive tote + bookmark set'
}, {
  id: '10',
  src: 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=400&q=80',
  alt: 'Reading by window',
  type: 'tagged',
  credit: '@morningpages',
  caption: 'Sunday mornings ☀️'
}, {
  id: '11',
  src: 'https://images.unsplash.com/photo-1476275466078-4007374efbbe?w=600&q=80',
  alt: 'Book and coffee',
  type: 'tagged',
  credit: '@brewandread',
  caption: 'The perfect combo'
}, {
  id: '12',
  src: 'https://images.unsplash.com/photo-1550399105-c4db5fb85c18?w=500&q=80',
  alt: 'Bookmarks collection',
  type: 'posted',
  caption: 'All our bookmark designs so far'
}];
export const GalleryTab: React.FC = () => {
  const {
    darkMode
  } = useDarkMode();
  const [filter, setFilter] = useState<GalleryFilter>('posted');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const filtered = galleryImages.filter(img => img.type === filter);
  const openLightbox = (idx: number) => setLightboxIndex(idx);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => {
    setLightboxIndex(i => i === null ? null : (i - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);
  const next = useCallback(() => {
    setLightboxIndex(i => i === null ? null : (i + 1) % filtered.length);
  }, [filtered.length]);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    if (lightboxIndex !== null) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, prev, next]);
  const selectedImage = lightboxIndex !== null ? filtered[lightboxIndex] : null;
  return <>
      <div data-id="element-6752" data-mp="239jsqw2n2hm5">
        {/* Filter toggle */}
        <div className="flex items-center justify-between mb-6" data-id="element-6753" data-mp="23al65774ng71">
          <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6754" data-mp="286tdvzizw746">
            Gallery
          </h2>
          <div className={`flex rounded-xl p-1 gap-1 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-6755" data-mp="2h89p29hmwd9g">
            {(['posted', 'tagged'] as GalleryFilter[]).map(f => <button key={f} onClick={() => {
            setFilter(f);
            setLightboxIndex(null);
          }} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === f ? darkMode ? 'bg-gray-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6756" data-mp="2m504culm74tt">
                {f === 'tagged' && <TagIcon className="w-3.5 h-3.5" data-id="element-6757" data-mp="5kxqewxr5x28" />}
                {f === 'posted' ? 'Our Posts' : 'Tagged In'}
              </button>)}
          </div>
        </div>

        {/* Masonry grid — CSS columns handles all image shapes naturally */}
        {filtered.length > 0 ? <div className="columns-2 sm:columns-3 lg:columns-4 gap-3" data-id="element-6758" data-mp="taidns0xvghb">
            {filtered.map((img, idx) => <button key={img.id} onClick={() => openLightbox(idx)} className="break-inside-avoid mb-3 block w-full rounded-xl overflow-hidden group relative focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500" data-id="element-6759" data-mp="p96uo5cklti3" data-mpi={idx}>
                <img src={img.src} alt={img.alt} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-6760" data-mp="3bgkvkj154fsp" />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-end p-3" data-id="element-6761" data-mp="31hlvvpycruhu">
                  <p className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 line-clamp-2 text-left" data-id="element-6762" data-mp="36j1elbd25ox2">
                    {img.caption}
                    {img.credit && <span className="block text-white/70 mt-0.5" data-id="element-6763" data-mp="2is5s8trdflx7">
                        {img.credit}
                      </span>}
                  </p>
                </div>
              </button>)}
          </div> : <div className={`text-center py-16 rounded-2xl border-2 border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-6764" data-mp="29tbpelng5zb7">
            <ImageIcon className={`h-12 w-12 mx-auto mb-3 opacity-40 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-6765" data-mp="1v51wwymj4b92" />
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-6766" data-mp="1q7igpt69toeh">
              No images yet
            </p>
          </div>}
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-6767" data-mp="1z4j4pypfhkhe">
        {selectedImage && <>
            {/* Backdrop */}
            <motion.div key="lb-backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/90 z-50" onClick={closeLightbox} data-id="element-6768" data-mp="2e1a7mhyb9ebr" />

            {/* Image panel */}
            <motion.div key="lb-panel" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.2,
          ease: [0.32, 0.72, 0, 1]
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-6769" data-mp="2rvuc7u1n1z9s">
              <div className="relative max-w-4xl w-full pointer-events-auto" onClick={e => e.stopPropagation()} data-id="element-6770" data-mp="3khazvj16kuod">
                <img src={selectedImage.src} alt={selectedImage.alt} className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" data-id="element-6771" data-mp="3u7vs8oq9h2yu" />

                {/* Caption */}
                {(selectedImage.caption || selectedImage.credit) && <div className="mt-3 text-center" data-id="element-6772" data-mp="2u7sand3yt5p5">
                    {selectedImage.caption && <p className="text-white text-sm font-medium" data-id="element-6773" data-mp="2p8fq4z0kgapq">
                        {selectedImage.caption}
                      </p>}
                    {selectedImage.credit && <p className="text-white/60 text-xs mt-0.5" data-id="element-6774" data-mp="2fbkr8f0ds8ax">
                        {selectedImage.credit}
                      </p>}
                  </div>}

                {/* Counter */}
                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full" data-id="element-6775" data-mp="26h6eok78jbvi">
                  {(lightboxIndex ?? 0) + 1} / {filtered.length}
                </div>

                {/* Close */}
                <button onClick={closeLightbox} className="absolute top-3 right-3 p-2 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors" data-id="element-6776" data-mp="1rkytbgk3c4en">
                  <XIcon className="w-5 h-5" data-id="element-6777" data-mp="3vxb62tsne2vx" />
                </button>

                {/* Prev */}
                {filtered.length > 1 && <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors" data-id="element-6778" data-mp="3qxopqbhiw6iw">
                    <ChevronLeftIcon className="w-5 h-5" data-id="element-6779" data-mp="3d0t8hvr4b8rr" />
                  </button>}

                {/* Next */}
                {filtered.length > 1 && <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors" data-id="element-6780" data-mp="3m0y2933e28ng">
                    <ChevronRightIcon className="w-5 h-5" data-id="element-6781" data-mp="118e5p6b23g51" />
                  </button>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
};